import { ReactNode, useMemo } from "react";
import { MenuProps } from "antd";
import {
  ShoppingBag,
  User,
  Tags,
  Star,
  Search,
  Percent,
  Home,
  MapPin,
  ShoppingCart,
  DollarSign,
  FileCheck,
  Wallet,
  Tag,
  Archive,
  Shield,
  TrendingUp,
  Image,
  Settings,
  BadgePercent,
  Banknote,
  MessageCircle,
  UtensilsCrossed,
  Users,
  Truck,
  CircleDollarSign,
} from "lucide-react";
import useAppDispatch from "../../../../redux/hooks/useAppDispatch";
import { useSelector } from "react-redux";
import {
  selectCollapsed,
  selectUser,
  uiChangeCollapsedState,
} from "../../../../redux";
import { Link, useLocation } from "react-router-dom";
import {
  DEFAULT_SELLER_MODULES,
} from "../../../config/adminModules";
import {
  filterMenuByModuleKeys,
  parseAllowedModules,
} from "../../../utils/filterAdminMenu";

// Wrapper component to maintain consistent icon sizing
const Icon = ({ children }: { children: ReactNode }) => (
  <span style={{ 
    display: 'inline-flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    fontSize: '16px',
    marginRight: '16px',
    minWidth: '20px'
  }}>
    {children}
  </span>
);

type MenuItem = Required<MenuProps>["items"][number];

export const useAdminMenuLayout = () => {
  const dispatch = useAppDispatch();
  const collapsed = useSelector(selectCollapsed);
  const authUser = useSelector(selectUser);
  let { pathname } = useLocation();

  function getItem(
    icon: ReactNode,
    label: ReactNode,
    key: string,
    children?: MenuItem[]
  ): MenuItem {
    return {
      icon,
      label,
      key,
      children,
    } as MenuItem;
  }

  let items: MenuItem[] = [
    getItem(
      <Icon><ShoppingCart size={16} /></Icon>,
      <Link to={"/orders"}>Pedidos</Link>,
      "/orders"
    ),
    getItem(
      <Icon><MessageCircle size={16} /></Icon>,
      <Link to={"/support-tickets"}>Mensajes</Link>,
      "/support-tickets"
    ),
    getItem(
      <Icon><ShoppingBag size={16} /></Icon>,
      <Link to={"/products"}>Productos</Link>,
      "/products"
    ),
    getItem(
      <Icon><TrendingUp size={16} /></Icon>,
      <Link to={"/popular-products"}>Productos populares</Link>,
      "/popular-products"
    ),
    getItem(
      <Icon><BadgePercent size={16} /></Icon>,
      <>Ofertas</>,
      "/combos-and-descount",
      [
        getItem(
          <Icon><Percent size={16} /></Icon>,
          <Link to={"/promotions"}>Promociones</Link>,
          "/promotions"
        ),
        getItem(
          <Icon><DollarSign size={16} /></Icon>,
          <Link to={"/promotions?promoType=2x1&from=legacy"}>Combo 2x1</Link>,
          "/promotions?promoType=2x1"
        ),
        getItem(
          <Icon><DollarSign size={16} /></Icon>,
          <Link to={"/promotions?promoType=3x2&from=legacy"}>Combo 3x2</Link>,
          "/promotions?promoType=3x2"
        ),
        getItem(
          <Icon><DollarSign size={16} /></Icon>,
          <Link to={"/rules-fifty-off/"}>Rollos 50%</Link>,
          "/rules-fifty-off/"
        ),
        getItem(
          <Icon><DollarSign size={16} /></Icon>,
          <Link to={"/rules-sushi-lunch/"}>Sushi Lunch</Link>,
          "/rules-sushi-lunch/"
        ),
      ]
    ),
    getItem(
      <Icon><MapPin size={16} /></Icon>,
      <Link to={"/stores"}>Sedes</Link>,
      "/stores"
    ),
    getItem(
      <Icon><Archive size={16} /></Icon>,
      <Link to={"/sauces"}>Salsas</Link>,
      "/sauces"
    ),
    getItem(
      <Icon><UtensilsCrossed size={16} /></Icon>,
      <Link to={"/accompaniments"}>Acompañamientos</Link>,
      "/accompaniments"
    ),
    getItem(
      <Icon><Search size={16} /></Icon>,
      <Link to={"/productCategories"}>Categorías</Link>,
      "/productCategories"
    ),
    // getItem(
    //   <Icon><FileText size={16} /></Icon>,
    //   <Link to={"/productCharacteristics"}>Características</Link>,
    //   "/productCharacteristics"
    // ),
    getItem(
      <Icon><Shield size={16} /></Icon>,
      <Link to={"/listCustomerGroups"}>Grupos de clientes</Link>,
      "/listCustomerGroups"
    ),
    getItem(
      <Icon><Tags size={16} /></Icon>,
      <Link to={"/discountCode"}>Cupones</Link>,
      "/discountCode"
    ),
    // getItem(<Icon><Store size={16} /></Icon>, <Link to={"/brands"}>Marcas</Link>, "/brands"),

    // getItem(<Icon><Hash size={16} /></Icon>, <>Academia</>, "/academy", [
    //   getItem(
    //     <Icon><Calendar size={16} /></Icon>,
    //     <Link to={"/courses"}>Cursos</Link>,
    //     "/courses"
    //   ),
    //   getItem(
    //     <Icon><ClipboardCheck size={16} /></Icon>,
    //     <Link to={"/coursesCategories"}>Categorías</Link>,
    //     "/coursesCategories"
    //   ),
    // ]),
    // getItem(<Icon><Hash size={16} /></Icon>, <>Trabajo</>, "/work", [
    //   getItem(
    //     <Icon><Briefcase size={16} /></Icon>,
    //     <Link to={"/workOffers"}>Ofert. de trabajo</Link>,
    //     "/workOffers"
    //   ),
    //   getItem(
    //     <Icon><Archive size={16} /></Icon>,
    //     <Link to={"/workOffersCategories"}>Categoría</Link>,
    //     "/workOffersCategories"
    //   ),
    // ]),
    getItem(
      <Icon><Star size={16} /></Icon>,
      <Link to={"/ratings"}>Calificaciones</Link>,
      "/ratings"
    ),
    getItem(<Icon><Banknote size={16} /></Icon>, <>Billetera</>, "/wallet", [
      getItem(
        <Icon><Wallet size={16} /></Icon>,
        <Link to={"/walletBalance"}>Balance</Link>,
        "/walletBalance"
      ),
      getItem(
        <Icon><Wallet size={16} /></Icon>,
        <Link to={"/walletMovements"}>Movimientos</Link>,
        "/walletMovements"
      ),
      getItem(
        <Icon><Tag size={16} /></Icon>,
        <Link to={"/walletBonus"}>Bonos</Link>,
        "/walletBonus"
      ),
    ]),
    // getItem(<Icon><Hash size={16} /></Icon>, <>Directorios</>, "/directory", [
    //   getItem(
    //     <Icon><FileCheck size={16} /></Icon>,
    //     <Link to={"/contactsDirectory"}>Profesional</Link>,
    //     "/contactsDirectory"
    //   ),
    //   getItem(
    //     <Icon><FileText size={16} /></Icon>,
    //     <Link to={"/directoryCategories"}>Categorías</Link>,
    //     "/directoryCategories"
    //   ),
    // ]),

    getItem(
      <Icon><Image size={16} /></Icon>,
      <Link to={"/banners"}>Banners</Link>,
      "/banners"
    ),
    getItem(<Icon><FileCheck size={16} /></Icon>, <Link to={"/cms"}>CMS</Link>, "/cms"),
    getItem(
      <Icon><User size={16} /></Icon>,
      <Link to={"/users"}>Usuarios del sistema</Link>,
      "/users"
    ),
    getItem(
      <Icon><Users size={16} /></Icon>,
      <Link to={"/clientes"}>Clientes</Link>,
      "/clientes"
    ),
    getItem(
      <Icon><Truck size={16} /></Icon>,
      <Link to={"/domiciliarios"}>Domiciliarios</Link>,
      "/domiciliarios"
    ),
    // getItem(
    //   <Icon><Target size={16} /></Icon>,
    //   <Link to={"/addressesUsers"}>Dirc. de usuarios</Link>,
    //   "/addressesUsers"
    // ),
    getItem(<Icon><Settings size={16} /></Icon>, <>Configuración</>, "/configurations", [
      getItem(<Icon><Percent size={16} /></Icon>, <Link to={"/tax"}>IVA</Link>, "/tax"),
      getItem(
        <Icon><Home size={16} /></Icon>,
        <Link to={"/cities"}>Ciudades</Link>,
        "/cities"
      ),
      getItem(
        <Icon><MapPin size={16} /></Icon>,
        <Link to={"/states"}>Estados</Link>,
        "/states"
      ),
      getItem(
        <Icon><CircleDollarSign size={16} /></Icon>,
        <Link to={"/shipping-rates"}>Tarifas de envío</Link>,
        "/shipping-rates"
      ),
      getItem(
        <Icon><TrendingUp size={16} /></Icon>,
        <Link to={"/systemVariables"}>Variables del sis.</Link>,
        "/systemVariables"
      ),
      getItem(
        <Icon><TrendingUp size={16} /></Icon>,
        <Link to={"/configuration-cashback"}>Config. cashback</Link>,
        "/configuration-cashback"
      ),
    ]),
  ];

  let items2: MenuItem[] = [
    getItem(
      <Icon><ShoppingCart size={16} /></Icon>,
      <Link to={"/orders"}>Pedidos</Link>,
      "/orders"
    ),
    getItem(
      <Icon><MessageCircle size={16} /></Icon>,
      <Link to={"/support-tickets"}>Mensajes</Link>,
      "/support-tickets"
    ),
  ];

  const menuItems = useMemo(() => {
    const role = authUser?.role;
    const customModules = parseAllowedModules(authUser?.allowed_modules);

    if (customModules?.length) {
      return filterMenuByModuleKeys(items, customModules);
    }

    if (role === "seller") {
      return filterMenuByModuleKeys(items2, DEFAULT_SELLER_MODULES);
    }

    return items;
  }, [authUser?.role, authUser?.allowed_modules, items, items2]);

  const changeCollapsed = () => {
    dispatch(uiChangeCollapsedState());
  };
  return {
    //state
    collapsed,
    items: menuItems,
    pathname,
    //methods
    //functions
    changeCollapsed,
  };
};
